import { useEffect, useState } from 'react';

/**
 * Returns the id of the section currently nearest the top of the viewport.
 * Picking the topmost intersecting entry (rather than the most visible one)
 * keeps the rail from flickering between two sections on a fast scroll.
 */
export function useActiveSection(ids: readonly string[]) {
  const [active, setActive] = useState<string | null>(null);

  useEffect(() => {
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (sections.length === 0 || !('IntersectionObserver' in window)) return;

    const visible = new Set<string>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        }

        const topmost = ids.find((id) => visible.has(id));
        setActive(topmost ?? null);
      },
      { rootMargin: '-20% 0px -70% 0px' },
    );

    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ids]);

  return active;
}
